import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAppContext } from '../../context/AppContext';
import {
  getAdminClassTypes,
  createAdminClassType,
  updateAdminClassType,
  deleteAdminClassType,
} from '../../services/adminService';

const EMPTY_FORM = {
  name: '',
  displayName: '',
  color: '',
  active: true,
  isIndividual: false,
  type: 'offline',
  description: '',
  locationName: '',
  locationUrl: '',
  videoCallUrl: '',
  durationMinutes: '',
  capacity: '',
  studioCost: '',
  allowJoinOnline: false,
};

const COLOR_OPTIONS = [
  { name: 'None', value: '' },
  { name: 'Clay', value: '#C17B5C' },
  { name: 'Sage', value: '#7A8C76' },
  { name: 'Gold', value: '#C4A96A' },
  { name: 'Red', value: '#EF4444' },
  { name: 'Orange', value: '#F97316' },
  { name: 'Amber', value: '#F59E0B' },
  { name: 'Yellow', value: '#EAB308' },
  { name: 'Green', value: '#22C55E' },
  { name: 'Teal', value: '#14B8A6' },
  { name: 'Blue', value: '#3B82F6' },
  { name: 'Indigo', value: '#6366F1' },
  { name: 'Purple', value: '#A855F7' },
  { name: 'Pink', value: '#EC4899' },
  { name: 'Gray', value: '#6B7280' },
];

export default function AdminClassTypes({ token }) {
  const { setToaster } = useAppContext();
  const [classTypes, setClassTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchClassTypes = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const data = await getAdminClassTypes(token);
      setClassTypes(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching class types:', error);
      setToaster({ message: 'Failed to load class types', type: 'error', show: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClassTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const openAdd = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (ct) => {
    setEditingId(ct._id);
    setForm({
      name: ct.name || '',
      displayName: ct.displayName || '',
      color: ct.color || '',
      active: ct.active !== false,
      isIndividual: ct.isIndividual === true,
      type: ct.type === 'online' ? 'online' : 'offline',
      description: ct.description || '',
      locationName: ct.locationName || '',
      locationUrl: ct.locationUrl || '',
      videoCallUrl: ct.videoCallUrl || '',
      durationMinutes: ct.durationMinutes ?? '',
      capacity: ct.capacity ?? '',
      studioCost: ct.studioCost ?? '',
      allowJoinOnline: ct.allowJoinOnline === true,
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        displayName: form.displayName.trim(),
        color: form.color.trim(),
        active: form.active,
        isIndividual: form.isIndividual,
        type: form.type === 'online' ? 'online' : 'offline',
        description: form.description.trim(),
        locationName: form.locationName.trim(),
        locationUrl: form.locationUrl.trim(),
        videoCallUrl: form.videoCallUrl.trim(),
        durationMinutes: form.durationMinutes === '' ? null : Number(form.durationMinutes),
        capacity: form.capacity === '' ? null : Number(form.capacity),
        studioCost: form.studioCost === '' ? null : Number(form.studioCost),
        allowJoinOnline: form.allowJoinOnline,
      };
      if (editingId) {
        await updateAdminClassType(token, editingId, payload);
        setToaster({ message: 'Class type updated', type: 'success', show: true });
      } else {
        await createAdminClassType(token, payload);
        setToaster({ message: 'Class type created', type: 'success', show: true });
      }
      closeForm();
      fetchClassTypes();
    } catch (error) {
      setToaster({ message: error.response?.data?.error || 'Failed to save class type', type: 'error', show: true });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (ct) => {
    if (!window.confirm(`Delete class type "${ct.displayName}"?`)) return;
    setDeletingId(ct._id);
    try {
      await deleteAdminClassType(token, ct._id);
      setToaster({ message: 'Class type deleted', type: 'success', show: true });
      fetchClassTypes();
    } catch (error) {
      setToaster({ message: error.response?.data?.error || 'Failed to delete class type', type: 'error', show: true });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-4 sm:p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Class types</h3>
        <button
          type="button"
          onClick={openAdd}
          className="px-3 py-1.5 bg-brown text-white rounded text-sm hover:opacity-90"
        >
          + Add class type
        </button>
      </div>

      {loading ? (
        <p className="text-center py-6 text-gray-500 text-sm">Loading…</p>
      ) : classTypes.length === 0 ? (
        <p className="text-center py-6 text-gray-500 text-sm">No class types yet.</p>
      ) : (
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-left text-sm border border-gray-200 rounded overflow-hidden min-w-[600px]">
            <thead className="border-b border-gray-200 bg-gray-50">
              <tr>
                <th className="py-2 px-3 font-semibold">Name</th>
                <th className="py-2 px-3 font-semibold">Display name</th>
                <th className="py-2 px-3 font-semibold">Type</th>
                <th className="py-2 px-3 font-semibold">Status</th>
                <th className="py-2 px-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {classTypes.map((ct) => (
                <tr key={ct._id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-2 px-3">{ct.name}</td>
                  <td className="py-2 px-3">{ct.displayName}</td>
                  <td className="py-2 px-3">
                    {ct.isIndividual ? 'Individual' : 'Group'}
                    {ct.allowJoinOnline && <span className="ml-1 text-blue-600" title="Allows joining online">🖥️</span>}
                  </td>
                  <td className="py-2 px-3">
                    {ct.active ? (
                      <span className="text-green-700">Active</span>
                    ) : (
                      <span className="text-gray-500">Inactive</span>
                    )}
                  </td>
                  <td className="py-2 px-3">
                    <button type="button" onClick={() => openEdit(ct)} className="text-brown hover:underline mr-2">
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(ct)}
                      disabled={deletingId === ct._id}
                      className="text-red-600 hover:underline disabled:opacity-50"
                    >
                      {deletingId === ct._id ? '…' : 'Delete'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && classTypes.length > 0 && (
        <div className="md:hidden divide-y divide-gray-100">
          {classTypes.map((ct) => (
            <div key={ct._id} className="py-3">
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-medium">{ct.displayName}</p>
                  <p className="text-sm text-gray-600">{ct.name}</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {ct.isIndividual ? 'Individual' : 'Group'} · {ct.active ? 'Active' : 'Inactive'}
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button type="button" onClick={() => openEdit(ct)} className="text-brown text-sm">Edit</button>
                  <button
                    type="button"
                    onClick={() => handleDelete(ct)}
                    disabled={deletingId === ct._id}
                    className="text-red-600 text-sm disabled:opacity-50"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && createPortal(
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-[1px] flex items-center justify-center p-4 z-[70] overflow-y-auto"
          onClick={closeForm}
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
        >
          <div
            className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold">{editingId ? 'Edit class type' : 'Add class type'}</h3>
              <button
                type="button"
                onClick={closeForm}
                className="text-gray-400 hover:text-gray-700 text-xl leading-none"
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-3">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Basic info</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Internal name *
                    </label>
                    <input
                      value={form.name}
                      onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brown/30 focus:border-brown"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Display name *</label>
                    <input
                      value={form.displayName}
                      onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brown/30 focus:border-brown"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Color</label>
                  <div className="flex flex-wrap gap-2">
                    {COLOR_OPTIONS.map((c) => (
                      <button
                        key={c.value || 'none'}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, color: c.value }))}
                        title={c.name}
                        className={`w-7 h-7 rounded-full border flex items-center justify-center transition ${
                          form.color === c.value
                            ? 'ring-2 ring-offset-1 ring-brown border-brown'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                        style={{ backgroundColor: c.value || '#FFFFFF' }}
                      >
                        {!c.value && <span className="text-gray-400 text-xs">–</span>}
                      </button>
                    ))}
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm pt-1">
                  <input
                    type="checkbox"
                    checked={form.active}
                    onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                    className="rounded border-gray-300 text-brown focus:ring-brown/30"
                  />
                  Active
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.isIndividual}
                    onChange={(e) => setForm((f) => ({ ...f, isIndividual: e.target.checked }))}
                    className="rounded border-gray-300 text-brown focus:ring-brown/30"
                  />
                  Individual class <span className="text-xs text-gray-400">(1-on-1 sessions, capacity 1)</span>
                </label>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select
                    value={form.type}
                    onChange={(e) => {
                      const type = e.target.value;
                      setForm((f) => ({ ...f, type, allowJoinOnline: type === 'online' ? false : f.allowJoinOnline }));
                    }}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brown/30 focus:border-brown"
                  >
                    <option value="offline">Offline</option>
                    <option value="online">Online</option>
                  </select>
                </div>
              </div>

              <div className="space-y-3 pt-4 border-t border-gray-100">
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Class defaults</p>
                  <p className="text-xs text-gray-400 mt-0.5">Prefilled automatically when this type is chosen while creating a class.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description <span className="text-xs text-gray-400">(optional)</span>
                  </label>
                  <input
                    value={form.description}
                    onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brown/30 focus:border-brown"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Location name <span className="text-xs text-gray-400">(optional)</span>
                  </label>
                  <input
                    value={form.locationName}
                    onChange={(e) => setForm((f) => ({ ...f, locationName: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brown/30 focus:border-brown"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Location URL <span className="text-xs text-gray-400">(optional)</span>
                  </label>
                  <input
                    type="url"
                    value={form.locationUrl}
                    onChange={(e) => setForm((f) => ({ ...f, locationUrl: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brown/30 focus:border-brown"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Video call link <span className="text-xs text-gray-400">(optional)</span>
                  </label>
                  <input
                    type="url"
                    value={form.videoCallUrl}
                    onChange={(e) => setForm((f) => ({ ...f, videoCallUrl: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brown/30 focus:border-brown"
                  />
                </div>
                {form.type !== 'online' && (
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={form.allowJoinOnline}
                      onChange={(e) => setForm((f) => ({ ...f, allowJoinOnline: e.target.checked }))}
                      className="rounded border-gray-300 text-brown focus:ring-brown/30"
                    />
                    Allow joining online
                    <span className="text-xs text-gray-400">(any active pass may attend remotely)</span>
                  </label>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Duration (min) <span className="text-xs text-gray-400">(optional)</span>
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={form.durationMinutes}
                      onChange={(e) => setForm((f) => ({ ...f, durationMinutes: e.target.value }))}
                      onWheel={(e) => e.currentTarget.blur()}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brown/30 focus:border-brown"
                      placeholder="60"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Capacity <span className="text-xs text-gray-400">(optional)</span>
                    </label>
                    <input
                      type="number"
                      value={form.capacity}
                      onChange={(e) => setForm((f) => ({ ...f, capacity: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brown/30 focus:border-brown"
                      placeholder="Unlimited"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Studio cost (₪) <span className="text-xs text-gray-400">(optional)</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={form.studioCost}
                    onChange={(e) => setForm((f) => ({ ...f, studioCost: e.target.value }))}
                    onWheel={(e) => e.currentTarget.blur()}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brown/30 focus:border-brown"
                    placeholder="Optional"
                  />
                </div>
              </div>

              <div className="flex gap-2 pt-4 border-t border-gray-100">
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-brown text-white rounded-lg text-sm font-medium hover:bg-pink transition disabled:opacity-50"
                >
                  {saving ? 'Saving…' : editingId ? 'Update' : 'Create'}
                </button>
                <button
                  type="button"
                  onClick={closeForm}
                  className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm font-medium transition"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
